import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, Send, ShieldCheck, AlertCircle, Palette, ExternalLink, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// One-time reminder card guiding the user to upload the Prestigious shield
// to their DocuSign account's Branding settings so sender pages + signing
// confirmation emails carry the logo. Dismiss per-browser via localStorage.
function BrandingReminder() {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("ds-branding-reminder-dismissed") === "1"
  );
  if (dismissed) return null;
  const dismiss = () => {
    localStorage.setItem("ds-branding-reminder-dismissed", "1");
    setDismissed(true);
  };
  return (
    <div
      className="rounded-lg border border-orange-500/30 bg-orange-500/[0.04] p-4 text-sm relative"
      data-testid="ds-branding-reminder"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
        data-testid="ds-branding-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-orange-500/15 flex items-center justify-center shrink-0">
          <Palette className="h-4 w-4 text-orange-500" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="font-semibold mb-1">One-time: brand your DocuSign account</div>
          <p className="text-muted-foreground leading-relaxed">
            The envelopes below carry Prestigious branding <em>inside</em> each
            PDF, but DocuSign&apos;s own sender + email pages default to their
            generic look. Upload the Prestigious shield to DocuSign&apos;s{" "}
            <b>Branding</b> settings so the signing page + confirmation emails
            match this portal&apos;s identity.
          </p>
          <ol className="mt-3 ml-5 text-xs text-muted-foreground list-decimal space-y-1">
            <li>Log in to DocuSign → <b>Admin</b> → <b>Signing &amp; Sending</b> → <b>Brands</b></li>
            <li>Click <b>Add Brand</b>, name it &ldquo;Prestigious HOA Portal&rdquo;</li>
            <li>Upload <code className="bg-black/10 dark:bg-white/10 px-1 rounded">/prestigious-192.png</code> (or the 512px variant) as the logo</li>
            <li>Set primary colour to <code className="bg-black/10 dark:bg-white/10 px-1 rounded">#8B1F26</code> (burgundy) and accent to <code className="bg-black/10 dark:bg-white/10 px-1 rounded">#F97316</code> (orange)</li>
            <li>Under &ldquo;Email Resource&rdquo;, replace the default logo with the same shield</li>
            <li>Save + set this brand as the account default</li>
          </ol>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <a
              href="https://apps.docusign.com/admin/brands"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-orange-600 dark:text-orange-400 hover:underline"
              data-testid="ds-branding-open"
            >
              Open DocuSign Branding <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href="/prestigious-512.png"
              download="prestigious-logo.png"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              data-testid="ds-branding-download"
            >
              Download shield PNG
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin UI for sending the SLA + DPA + MSA + Order Form as one DocuSign
// envelope to a prospective customer. Provider countersigner is optional —
// if omitted, the envelope routes straight to the customer.
export default function CustomerPacketPage() {
  const [status, setStatus] = useState(null);
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_title: "",
    provider_countersigner_name: "",
    provider_countersigner_email: "",
    order_form_no: "",
    tier: "Business",
    monthly_fee_usd: "",
    initial_term_months: 12,
  });

  const api = axios.create({
    baseURL: API,
    withCredentials: true, // Auth via httpOnly access_token cookie — no JS-readable token.
  });

  const refresh = async () => {
    try {
      const [s, e] = await Promise.all([
        api.get("/api/docusign/status"),
        api.get("/api/docusign/envelopes"),
      ]);
      setStatus(s.data);
      // Show only customer-packet envelopes on this page
      setEnvelopes((e.data.items || []).filter((i) => i.kind === "customer_packet"));
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/CustomerPacketPage.jsx: swallowed', err); }
  };

  useEffect(() => {
    refresh();
  }, []);

  const send = async (evt) => {
    evt.preventDefault();
    if (!form.customer_name || !form.customer_email) {
      toast.error("Customer name + email are required");
      return;
    }
    setLoading(true);
    try {
      const body = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_title: form.customer_title || null,
        provider_countersigner_name: form.provider_countersigner_name || null,
        provider_countersigner_email: form.provider_countersigner_email || null,
        order_form_no: form.order_form_no || null,
        tier: form.tier || null,
        monthly_fee_usd: form.monthly_fee_usd
          ? parseFloat(form.monthly_fee_usd)
          : null,
        initial_term_months: form.initial_term_months
          ? parseInt(form.initial_term_months, 10)
          : null,
      };
      const { data } = await api.post("/api/docusign/customer-packet", body);
      toast.success(`Envelope ${data.envelope_id?.slice(0, 8) || "?"} sent to ${data.customer_email}`);
      setForm({
        ...form,
        customer_name: "",
        customer_email: "",
        customer_title: "",
        order_form_no: "",
      });
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (typeof d === "object" && d?.error === "docusign_not_configured") {
        toast.error(`DocuSign not configured — missing: ${d.missing.join(", ")}`);
      } else {
        toast.error(typeof d === "string" ? d : "Send failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const isConfigured = status?.configured;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="customer-packet-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-orange-400 mb-1.5">
            Sales · DocuSign
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-semibold leading-tight">
            Send customer packet
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            One envelope, four PDFs — SLA · DPA · MSA · Order Form. Provider
            can countersign first (optional), then it routes to the customer
            with pre-populated commercial terms.
          </p>
        </div>
        {status && (
          <div
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest ${
              isConfigured
                ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
                : "border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300"
            }`}
            data-testid="ds-status-chip"
          >
            {isConfigured ? (
              <>
                <ShieldCheck className="h-3.5 w-3.5" />
                DocuSign {status.environment} ready
              </>
            ) : (
              <>
                <AlertCircle className="h-3.5 w-3.5" />
                DocuSign not configured
              </>
            )}
          </div>
        )}
      </div>

      {!isConfigured && status && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm" data-testid="ds-not-configured">
          <div className="font-semibold text-amber-600 dark:text-amber-300 mb-1">
            DocuSign integration is not configured yet.
          </div>
          <div className="text-muted-foreground">
            Provide the missing keys in <b>Admin → Settings → DocuSign</b>: {" "}
            {status.missing?.join(", ")}. Once set, come back to this page to
            send your first envelope.
          </div>
        </div>
      )}

      {/* Branding reminder — once + dismissible per browser */}
      <BrandingReminder />

      {/* Form */}
      <form
        onSubmit={send}
        className="rounded-xl border bg-card p-6 space-y-6"
        data-testid="customer-packet-form"
      >
        <div>
          <div className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-orange-500" />
            Customer
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="text-muted-foreground">Legal name *</span>
              <input
                type="text"
                required
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Ridgewood HOA, Inc."
                data-testid="cp-customer-name"
              />
            </label>
            <label className="block text-sm">
              <span className="text-muted-foreground">Signer email *</span>
              <input
                type="email"
                required
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="board-president@ridgewood.example"
                data-testid="cp-customer-email"
              />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="text-muted-foreground">Signer title (optional)</span>
              <input
                type="text"
                value={form.customer_title}
                onChange={(e) => setForm({ ...form, customer_title: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Board President"
                data-testid="cp-customer-title"
              />
            </label>
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold mb-3">Provider countersigner (optional)</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="text-muted-foreground">Name</span>
              <input
                type="text"
                value={form.provider_countersigner_name}
                onChange={(e) => setForm({ ...form, provider_countersigner_name: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Your name"
                data-testid="cp-provider-name"
              />
            </label>
            <label className="block text-sm">
              <span className="text-muted-foreground">Email</span>
              <input
                type="email"
                value={form.provider_countersigner_email}
                onChange={(e) => setForm({ ...form, provider_countersigner_email: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="you@prestigiouspm.net"
                data-testid="cp-provider-email"
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Leave blank to skip the countersign step and route the envelope
            directly to the customer.
          </p>
        </div>

        <div>
          <div className="text-sm font-semibold mb-3">Order Form terms</div>
          <div className="grid sm:grid-cols-4 gap-4">
            <label className="block text-sm">
              <span className="text-muted-foreground">Order # (optional)</span>
              <input
                type="text"
                value={form.order_form_no}
                onChange={(e) => setForm({ ...form, order_form_no: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="PRS-2026-001"
                data-testid="cp-order-no"
              />
            </label>
            <label className="block text-sm">
              <span className="text-muted-foreground">Tier</span>
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                data-testid="cp-tier"
              >
                <option value="Standard">Standard</option>
                <option value="Business">Business</option>
                <option value="Enterprise">Enterprise</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-muted-foreground">Monthly fee (USD)</span>
              <input
                type="number"
                step="0.01"
                value={form.monthly_fee_usd}
                onChange={(e) => setForm({ ...form, monthly_fee_usd: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="399.00"
                data-testid="cp-monthly-fee"
              />
            </label>
            <label className="block text-sm">
              <span className="text-muted-foreground">Initial term (months)</span>
              <input
                type="number"
                value={form.initial_term_months}
                onChange={(e) => setForm({ ...form, initial_term_months: e.target.value })}
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                data-testid="cp-term-months"
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            These pre-populate onto the Order Form (page 1) as locked read-only
            fields so the customer sees exactly the deal they&apos;re signing.
          </p>
        </div>

        <div className="flex items-center justify-end pt-2">
          <button
            type="submit"
            disabled={loading || !isConfigured}
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium px-6 py-2.5 text-sm transition-colors"
            data-testid="cp-send-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> Send envelope
              </>
            )}
          </button>
        </div>
      </form>

      {/* Envelope history */}
      {envelopes.length > 0 && (
        <div className="rounded-xl border bg-card overflow-hidden" data-testid="cp-history">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div className="text-sm font-semibold">Recent customer envelopes</div>
            <div className="text-xs text-muted-foreground">{envelopes.length} sent</div>
          </div>
          <div className="divide-y">
            {envelopes.map((e) => (
              <div
                key={e.envelope_id}
                className="px-6 py-3 flex items-center justify-between gap-4 flex-wrap"
                data-testid={`cp-history-row-${e.envelope_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {e.customer?.name || "?"} · {e.customer?.email}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {e.commercial_terms?.tier} ·{" "}
                    {e.commercial_terms?.monthly_fee_usd
                      ? `$${e.commercial_terms.monthly_fee_usd.toFixed(2)}/mo`
                      : "no fee set"} · {new Date(e.created_at).toLocaleString()}
                  </div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-1 ${
                    e.status === "completed"
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
                      : e.status === "declined" || e.status === "voided"
                      ? "bg-red-500/10 text-red-600 dark:text-red-300"
                      : "bg-orange-500/10 text-orange-600 dark:text-orange-300"
                  }`}
                >
                  {e.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
